// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { ChargeFilter } from '@/billing/api/get-charges';
import {
  ChargeListItem,
  ChargeSortProperty,
  chargeSortPropertySchema,
} from '@/billing/api/types';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import { CancelChargeDialog } from '@/billing/components/cancel-charge-dialog';
import { ChargeActionMenu } from '@/billing/components/charge-action-menu';
import { ChargeAuditDrawer } from '@/billing/components/charge-audit-drawer';
import {
  BILL_DATE_SHORTCUTS,
  CHARGE_ACTIONS_COL_WIDTH,
  CHARGE_BILLABLE_DATE_COL_WIDTH,
  CHARGE_STATUS_COL_WIDTH,
  PAGE_SIZE,
  STATUS_CHIP_CONFIG,
  STATUS_FILTER_OPTIONS,
} from '@/billing/components/charge-status-constants';
import { ChargeSummaryCards } from '@/billing/components/charge-summary-cards';
import { ChargeTableRow } from '@/billing/components/charge-table-row';
import { ChargeTriggerTypeFilterMenu } from '@/billing/components/charge-trigger-type-filter-menu';
import { GenerateInvoicesDialog } from '@/billing/components/generate-invoices-dialog';
import { MarkChargeBillableDialog } from '@/billing/components/mark-charge-billable-dialog';
import { MarkChargeNotBillableDialog } from '@/billing/components/mark-charge-not-billable-dialog';
import { RevertChargeCancellationDialog } from '@/billing/components/revert-charge-cancellation-dialog';
import { useCharges } from '@/billing/hooks/use-charges';
import { EducationalTooltip } from '@/components/educational-tooltip';
import { EmptyState } from '@/components/empty-state';
import {
  AmountRange,
  FilterAmountRangePopover,
} from '@/components/filter-amount-range-popover';
import { FilterDateRangePopover } from '@/components/filter-date-range-popover';
import { FilterMenu } from '@/components/filter-menu';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { Link } from '@/components/link';
import { PageContent } from '@/components/page-content';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import { SupportSnackbar } from '@/components/support-snackbar';
import { TableControlContainer } from '@/components/table-control-container';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ChargeStatus, chargeStatusSchema } from '@/constants/charge-status';
import { chargeTriggerTypeSchema } from '@/constants/charge-trigger-type';
import {
  ArrowForwardRounded,
  ClearRounded,
  InfoOutlined,
  SearchRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort, invert, sortDirectionSchema } from '@/constants/sort';
import { CustomerFilterMenu } from '@/customer/components/customer-filter-menu';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTableSort } from '@/hooks/use-table-sort';
import { getSparseProjects } from '@/project/api/get-sparse-projects';
import { searchTermSchema } from '@/utilities/search-term';
import {
  FIFTEEN_MINUTES_MS,
  FIVE_MINUTES_MS,
  TWO_MINUTES_MS,
} from '@/utilities/time-constants';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

const searchSchema = z.object({
  status: chargeStatusSchema.optional(),
  // May contain the NOT_SET_SENTINEL alongside real customer keys. The
  // request sends the sentinel as its own no-customer flag, not as a key.
  customerKeys: z.array(z.string()).optional(),
  projectKeys: z.array(z.string()).optional(),
  triggerTypes: z.array(chargeTriggerTypeSchema).optional(),
  query: searchTermSchema,
  page: z.number().optional(),
  billDateStart: z.string().optional().catch(undefined),
  billDateEnd: z.string().optional().catch(undefined),
  amountMin: z.number().optional().catch(undefined),
  amountMax: z.number().optional().catch(undefined),
  sortBy: chargeSortPropertySchema.optional().catch(undefined),
  sortDirection: sortDirectionSchema.optional(),
});

export const Route = createFileRoute('/_app/billing/charges')({
  component: ChargesRoute,
  validateSearch: searchSchema,
});

// ---------------------------------------------------------------------------
// Amount filter bounds derivation
// ---------------------------------------------------------------------------

// Fallback bounds used when the listing response has not arrived yet or the
// org has zero charges. The filter renders disabled in this state so the
// numeric values are only used to keep the MUI Slider from crashing on an
// empty range.
const AMOUNT_FILTER_FALLBACK_MIN = 0;
const AMOUNT_FILTER_FALLBACK_MAX = 1;
// Whole-dollar granularity — the smallest slider step and the integer unit
// enforced by the paired min/max inputs. Cents are intentionally not
// exposed here; the slider would be too twitchy to drag at cent precision.
const AMOUNT_FILTER_MIN_STEP = 1;
// Divisor for deriving the slider step from the [min, max] spread. A value
// of 20 gives users 20 tick positions across the range, which matches the
// review spec and keeps the granularity coarse enough to be draggable.
const AMOUNT_FILTER_STEP_DIVISOR = 20;
// Used to widen a zero-width range (single-charge org, or every charge at
// the same amount) just enough for the slider to render usefully.
const AMOUNT_FILTER_SINGLE_POINT_PAD = 1;

// Number of columns in the charge table — drives the cold-load skeleton
// so the placeholder row widths match the real table layout. Keep in sync
// with the <TableHead> below.
const CHARGE_COLUMN_COUNT = 9;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ChargesRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const globalNavigate = useNavigate();

  // Sort state derived from URL
  const sort: Sort<ChargeSortProperty> = useMemo(
    () => ({
      property: search.sortBy ?? 'billDate',
      direction: search.sortDirection ?? 'desc',
    }),
    [search.sortBy, search.sortDirection]
  );
  const { getSortDirection, getLabelDirection, isLabelActive } =
    useTableSort(sort);

  const createSortHandler = useCallback(
    (property: ChargeSortProperty) => () => {
      const newDirection =
        sort.property === property ? invert(sort.direction) : 'asc';
      void navigate({
        search: {
          ...search,
          sortBy: property,
          sortDirection: newDirection,
          page: undefined,
        },
      });
    },
    [sort, search, navigate]
  );

  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const generateDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const revertCancellationDialog = useDisclosure();
  const markBillableDialog = useDisclosure();
  const markNotBillableDialog = useDisclosure();
  const auditDrawer = useDisclosure();
  // Charge selected for the audit drawer. Persisted separately from the
  // action menu state so the drawer keeps showing the right log after the
  // popover unmounts.
  const [auditTarget, setAuditTarget] = useState<ChargeListItem | null>(null);

  // Action menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuChargeId, setMenuChargeId] = useState<string | null>(null);
  // Charge selected for the cancel dialog. Persisted separately so the dialog
  // continues to render its label after the action menu closes.
  const [cancelTarget, setCancelTarget] = useState<ChargeListItem | null>(null);
  // Same pattern for the revert-cancellation confirmation dialog.
  const [revertCancellationTarget, setRevertCancellationTarget] =
    useState<ChargeListItem | null>(null);
  // Same pattern for the mark-billable dialog. The dialog needs the charge's
  // pricing mode and current quantity / total to seed its inputs and preview.
  const [markBillableTarget, setMarkBillableTarget] =
    useState<ChargeListItem | null>(null);
  // Same pattern for the mark-not-billable confirmation dialog — persists
  // the charge label for the confirmation copy after the action menu closes.
  const [markNotBillableTarget, setMarkNotBillableTarget] =
    useState<ChargeListItem | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const activeStatus: ChargeStatus | 'all' = search.status ?? 'all';
  // The URL's customerKeys array may contain NOT_SET_SENTINEL; it lives in
  // the same array as real keys and the request sends it as its own flag.
  // The dropdown reads it as a normal option id.
  const activeCustomerKeys = search.customerKeys ?? [];
  const activeProjectKeys = search.projectKeys ?? [];
  const activeTriggerTypes = search.triggerTypes ?? [];
  const page = search.page ?? 1;
  const billDateRange: DateRange<DateTime> = [
    search.billDateStart ? DateTime.fromISO(search.billDateStart) : null,
    search.billDateEnd ? DateTime.fromISO(search.billDateEnd) : null,
  ];

  const isBillDateFilterActive =
    billDateRange[0] !== null || billDateRange[1] !== null;

  const amountRange: AmountRange = [
    search.amountMin ?? null,
    search.amountMax ?? null,
  ];

  // -------------------------------------------------------------------------
  // Fetch charge data
  // -------------------------------------------------------------------------

  const chargeFilter: ChargeFilter = {
    pageSize: PAGE_SIZE,
    pageNumber: page,
    sort,
    search: search.query,
    status: activeStatus === 'all' ? undefined : activeStatus,
    customerKeys:
      activeCustomerKeys.length > 0 ? activeCustomerKeys : undefined,
    projectKeys: activeProjectKeys.length > 0 ? activeProjectKeys : undefined,
    triggerTypes:
      activeTriggerTypes.length > 0 ? activeTriggerTypes : undefined,
    billDateStart: search.billDateStart,
    billDateEnd: search.billDateEnd,
    amountMin: search.amountMin,
    amountMax: search.amountMax,
  };

  const {
    charges,
    totalCount,
    isLoading,
    isPlaceholderData,
    amountRange: listingAmountRange,
  } = useCharges({
    filter: chargeFilter,
  });
  const downloadURL = new URL(
    '/v1/export/charges',
    import.meta.env.VITE_API_URL
  );

  // Data-driven bounds for the amount filter slider. The backend computes
  // amountRange over ALL charges (ignoring listing filters) so the slider
  // extents stay stable as the user narrows the view. When the listing
  // response has not yet arrived, or when there are no charges at all, fall
  // back to a safe [0, 1] range with the control disabled. A single-charge
  // org (or any org where every charge is the same amount) is still
  // "ready" — we widen the range by a single pad unit so the slider has
  // room to render and the user can still see the anchored value.
  const amountFilterBounds = useMemo(() => {
    const fallback = {
      min: AMOUNT_FILTER_FALLBACK_MIN,
      max: AMOUNT_FILTER_FALLBACK_MAX,
      step: AMOUNT_FILTER_MIN_STEP,
      isReady: false,
    };
    if (!listingAmountRange) {
      return fallback;
    }
    const rawMin = Number(listingAmountRange.min);
    const rawMax = Number(listingAmountRange.max);
    if (Number.isNaN(rawMin) || Number.isNaN(rawMax) || rawMax < rawMin) {
      return fallback;
    }
    // Integer bounds and step so the slider and paired inputs can only
    // produce whole-dollar values. Floor/ceil ensures the slider's extents
    // always fully cover the data range even when the raw values include
    // cents (e.g. $12.34 → min 12, max 13).
    const integerMin = Math.floor(rawMin);
    const integerMax = Math.ceil(rawMax);
    if (integerMax === integerMin) {
      return {
        min: integerMin,
        max: integerMax + AMOUNT_FILTER_SINGLE_POINT_PAD,
        step: AMOUNT_FILTER_MIN_STEP,
        isReady: true,
      };
    }
    const step = Math.max(
      AMOUNT_FILTER_MIN_STEP,
      Math.round((integerMax - integerMin) / AMOUNT_FILTER_STEP_DIVISOR)
    );
    return { min: integerMin, max: integerMax, step, isReady: true };
  }, [listingAmountRange]);

  // Page-overshoot recovery should live in the shared table footer instead of
  // route-specific guards. See ai/todo/pagination-footer-overshoot-recovery.md.

  // Summary cards always show org-wide totals regardless of the active filter
  // state. The filtered status summary returned by the listing endpoint is
  // intentionally ignored here — we may revisit this once we decide how the
  // cards should respond to filters. The same unfiltered fetch also feeds the
  // generate-invoices dialog's billable count, since the sweep endpoint
  // processes ALL billable charges regardless of UI filters.
  const { statusSummary: unfilteredSummary } = useCharges({
    filter: { pageSize: 1 },
  });
  // The sweep endpoint processes ALL billable charges regardless of currency,
  // so the gate sums the billable count across every currency bucket.
  const billableCount = unfilteredSummary
    ? unfilteredSummary.byCurrency.reduce(
        (sum, bucket) => sum + bucket.billableCount,
        0
      )
    : 0;

  // -------------------------------------------------------------------------
  // Fetch filter options
  // -------------------------------------------------------------------------

  const { data: projectsData } = useQuery({
    queryKey: ['sparse-projects', 'filter-options'],
    queryFn: getSparseProjects,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIFTEEN_MINUTES_MS,
    refetchOnWindowFocus: true,
    refetchInterval: TWO_MINUTES_MS,
  });
  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    id: p.key,
    label: p.name,
    isActive: activeProjectKeys.includes(p.key),
  }));

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleStatusChange = (newStatus: ChargeStatus | 'all') => {
    void navigate({
      search:
        newStatus === 'all'
          ? { ...omit(search, 'status', 'page') }
          : { ...search, status: newStatus, page: undefined },
    });
  };

  const handleChangeCustomer = (customerKeys: string[]) => {
    void navigate({
      search:
        customerKeys.length === 0
          ? { ...omit(search, 'customerKeys', 'page') }
          : { ...search, customerKeys, page: undefined },
    });
  };

  const handleChangeProject = (projectKeys: string[]) => {
    void navigate({
      search:
        projectKeys.length === 0
          ? { ...omit(search, 'projectKeys', 'page') }
          : { ...search, projectKeys, page: undefined },
    });
  };

  const [searchInput, setSearchInput] = useState(search.query ?? '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  const handleSearch = (query: string) => {
    setSearchInput(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const latestSearch = searchRef.current;
      void navigate({
        search:
          query.length === 0
            ? { ...omit(latestSearch, 'query', 'page') }
            : { ...latestSearch, query, page: undefined },
      });
    }, 300);
  };

  const handleChangeBillDateRange = (dateRange: DateRange<DateTime>) => {
    void navigate({
      search: {
        ...omit(search, ['billDateStart', 'billDateEnd', 'page']),
        billDateStart: dateRange[0]?.toISODate() ?? undefined,
        billDateEnd: dateRange[1]?.toISODate() ?? undefined,
      },
    });
  };

  const handleChangeAmountRange = (range: AmountRange) => {
    void navigate({
      search: {
        ...omit(search, ['amountMin', 'amountMax', 'page']),
        amountMin: range[0] ?? undefined,
        amountMax: range[1] ?? undefined,
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    void navigate({
      search:
        newPage === 1
          ? { ...omit(search, 'page') }
          : { ...search, page: newPage },
    });
  };

  const handleOpenMenu = (event: MouseEvent<HTMLElement>, chargeId: string) => {
    setMenuAnchor(event.currentTarget);
    setMenuChargeId(chargeId);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuChargeId(null);
  };

  const menuCharge =
    charges?.find((c: ChargeListItem) => c.id === menuChargeId) ?? null;

  const handleViewBillingPlan = () => {
    if (menuCharge) {
      // Pass focusChargeKey so the project page opens the line item drawer,
      // scrolls the originating charge into view, and briefly highlights it.
      void globalNavigate({
        to: '/projects/$projectKey',
        params: { projectKey: menuCharge.project.key },
        search: {
          lineItemKey: menuCharge.lineItemKey,
          focusChargeKey: menuCharge.key,
        },
      });
    }
    handleCloseMenu();
  };

  const handleCancelCharge = () => {
    if (!menuCharge) {
      handleCloseMenu();
      return;
    }
    // Capture the menu's selected charge before closing it, then open the
    // confirmation dialog. cancelTarget keeps the label visible after the
    // popover unmounts.
    setCancelTarget(menuCharge);
    handleCloseMenu();
    cancelDialog.open();
  };

  const handleMarkBillable = () => {
    if (!menuCharge) {
      handleCloseMenu();
      return;
    }
    // Capture the menu's selected charge before closing it, then open the
    // mark-billable dialog. The dialog drives the actual mutation so it can
    // collect bill date / quantity / override price first.
    setMarkBillableTarget(menuCharge);
    handleCloseMenu();
    markBillableDialog.open();
  };

  const handleMarkNotBillable = () => {
    if (!menuCharge) {
      handleCloseMenu();
      return;
    }
    // Capture the menu's selected charge before closing it, then open the
    // confirmation dialog. The mutation only fires once the user confirms.
    setMarkNotBillableTarget(menuCharge);
    handleCloseMenu();
    markNotBillableDialog.open();
  };

  const handleViewAuditLog = () => {
    if (!menuCharge) {
      handleCloseMenu();
      return;
    }
    setAuditTarget(menuCharge);
    handleCloseMenu();
    auditDrawer.open();
  };

  const handleRevertCancellation = () => {
    if (!menuCharge) {
      handleCloseMenu();
      return;
    }
    // Capture the menu's selected charge before closing it, then open the
    // confirmation dialog. The target keeps the label visible after the
    // popover unmounts.
    setRevertCancellationTarget(menuCharge);
    handleCloseMenu();
    revertCancellationDialog.open();
  };

  const isAmountFilterActive =
    search.amountMin !== undefined || search.amountMax !== undefined;

  const hasActiveFilters =
    activeCustomerKeys.length > 0 ||
    activeProjectKeys.length > 0 ||
    activeTriggerTypes.length > 0 ||
    (search.query ?? '').length > 0 ||
    isBillDateFilterActive ||
    isAmountFilterActive;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <PageContent>
      {/* Header */}
      <Stack sx={{ mb: 2 }}>
        <SectionBreadcrumbs
          items={[BILLING_BREADCRUMB_ROOT, { label: 'Charges' }]}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Charges</Typography>
          <Button
            component={Link}
            to="/billing/invoices"
            size="small"
            endIcon={<ArrowForwardRounded fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            View invoices
          </Button>
        </Stack>
      </Stack>

      {/* Summary cards */}
      <ChargeSummaryCards summary={unfilteredSummary} />

      <Stack spacing={2}>
        {/* Generate invoices dialog */}
        <GenerateInvoicesDialog
          isOpen={generateDialog.isOpen}
          onClose={generateDialog.close}
          billableCount={billableCount}
        />

        {/* Status toggles + info icon + generate invoices */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterToggleGroup
              value={activeStatus}
              options={STATUS_FILTER_OPTIONS}
              onChange={handleStatusChange}
              ariaLabel="charge status filter"
            />
            <IconButton
              ref={infoButtonRef}
              size="small"
              onClick={() => setInfoOpen(true)}
              aria-label="Learn about charge statuses"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Stack>
          <Tooltip
            arrow
            title={
              !isLoading && billableCount === 0
                ? 'No billable charges available'
                : ''
            }
          >
            <Box component="span" sx={{ display: 'inline-block' }}>
              <Button
                variant="contained"
                size="small"
                onClick={generateDialog.open}
                disabled={isLoading || billableCount === 0}
              >
                Generate invoices
              </Button>
            </Box>
          </Tooltip>
        </Stack>

        <EducationalTooltip
          title="Charge statuses"
          isOpen={infoOpen}
          onClose={() => setInfoOpen(false)}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>Planned:</strong> Charge is scheduled but not yet billable
            </Typography>
            <Typography variant="body2">
              <strong>Billable:</strong> Charge is ready to be added to an
              invoice
            </Typography>
            <Typography variant="body2">
              <strong>Invoiced:</strong> Charge is on an invoice
            </Typography>
            <Typography variant="body2">
              <strong>Cancelled:</strong> Charge has been cancelled and will not
              be billed
            </Typography>
          </Stack>
        </EducationalTooltip>

        {/* Filter row */}
        <TableControlContainer paddingTop={0}>
          <Stack direction="row" spacing={2} flexGrow={1} alignItems="center">
            <CustomerFilterMenu
              label="Customer"
              identifier="key"
              onlyCustomers
              includeNotSet
              value={activeCustomerKeys}
              onChange={handleChangeCustomer}
            />
            <FilterMenu
              label="Project"
              isSearchable
              options={projectOptions}
              onChangeOption={(id, isActive) => {
                handleChangeProject(
                  isActive
                    ? [...activeProjectKeys, id]
                    : activeProjectKeys.filter((p) => p !== id)
                );
              }}
            />
            <ChargeTriggerTypeFilterMenu
              value={activeTriggerTypes}
              onChange={(next) => {
                void navigate({
                  search: {
                    ...search,
                    triggerTypes: next.length > 0 ? next : undefined,
                    page: undefined,
                  },
                });
              }}
            />
            <FilterDateRangePopover
              label="Billable date"
              value={billDateRange}
              onChange={handleChangeBillDateRange}
              shortcuts={BILL_DATE_SHORTCUTS}
            />
            <FilterAmountRangePopover
              label="Amount"
              min={amountFilterBounds.min}
              max={amountFilterBounds.max}
              step={amountFilterBounds.step}
              isDisabled={!amountFilterBounds.isReady}
              value={amountRange}
              onChange={handleChangeAmountRange}
            />
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={() => {
                  setSearchInput('');
                  void navigate({
                    search: omit(search, [
                      'customerKeys',
                      'projectKeys',
                      'triggerTypes',
                      'query',
                      'page',
                      'billDateStart',
                      'billDateEnd',
                      'amountMin',
                      'amountMax',
                    ]),
                  });
                }}
              >
                Clear filters
              </Button>
            )}
          </Stack>
          <TextField
            size="small"
            placeholder="Search charges"
            value={searchInput}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    fontSize="small"
                    sx={{ color: 'neutral.400' }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleSearch('')}
                    edge="end"
                    size="small"
                  >
                    <ClearRounded />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ width: { xs: '100%', sm: '20rem' } }}
          />
        </TableControlContainer>

        {/* Charges table — sits flush against the filter row above. While
            the first fetch is in flight `charges` is `null`; we render
            skeleton rows so the empty state doesn't flash as a lie. The
            same skeleton treatment applies when a filter change supplies
            placeholder data from a previous query key (`isPlaceholderData`)
            and there are no rows to show — without it the empty state
            flashes before the new payload arrives. `isPlaceholderData` is
            used instead of `isFetching` so that background refetches of
            an already-resolved empty result do not briefly replace the
            empty state with skeletons. */}
        {charges !== null && charges.length === 0 && !isPlaceholderData ? (
          <EmptyState
            title={
              activeStatus !== 'all'
                ? `No ${STATUS_CHIP_CONFIG[activeStatus].label.toLowerCase()} charges found`
                : 'No charges found'
            }
          />
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mt: '0 !important' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    width="12%"
                    sortDirection={getSortDirection('customer')}
                  >
                    <TableSortLabel
                      active={isLabelActive('customer')}
                      direction={getLabelDirection('customer')}
                      onClick={createSortHandler('customer')}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="21%"
                    sortDirection={getSortDirection('project')}
                  >
                    <TableSortLabel
                      active={isLabelActive('project')}
                      direction={getLabelDirection('project')}
                      onClick={createSortHandler('project')}
                    >
                      Project
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="17%"
                    sortDirection={getSortDirection('label')}
                  >
                    <TableSortLabel
                      active={isLabelActive('label')}
                      direction={getLabelDirection('label')}
                      onClick={createSortHandler('label')}
                    >
                      Charge label
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="8%"
                    sortDirection={getSortDirection('item')}
                  >
                    <TableSortLabel
                      active={isLabelActive('item')}
                      direction={getLabelDirection('item')}
                      onClick={createSortHandler('item')}
                    >
                      Item
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="8%"
                    align="right"
                    sortDirection={getSortDirection('amount')}
                  >
                    <TableSortLabel
                      active={isLabelActive('amount')}
                      direction={getLabelDirection('amount')}
                      onClick={createSortHandler('amount')}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="13%"
                    sortDirection={getSortDirection('servicePeriodStart')}
                  >
                    <TableSortLabel
                      active={isLabelActive('servicePeriodStart')}
                      direction={getLabelDirection('servicePeriodStart')}
                      onClick={createSortHandler('servicePeriodStart')}
                    >
                      Service period
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width={CHARGE_BILLABLE_DATE_COL_WIDTH}
                    sortDirection={getSortDirection('billDate')}
                  >
                    <TableSortLabel
                      active={isLabelActive('billDate')}
                      direction={getLabelDirection('billDate')}
                      onClick={createSortHandler('billDate')}
                    >
                      Billable date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width={CHARGE_STATUS_COL_WIDTH}
                    sortDirection={getSortDirection('status')}
                  >
                    <TableSortLabel
                      active={isLabelActive('status')}
                      direction={getLabelDirection('status')}
                      onClick={createSortHandler('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width={CHARGE_ACTIONS_COL_WIDTH} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {charges === null || (charges.length === 0 && isPlaceholderData)
                  ? Array.from({ length: PAGE_SIZE }).map((_, rowIndex) => (
                      <TableRow key={`skeleton-${rowIndex}`}>
                        {Array.from({ length: CHARGE_COLUMN_COUNT }).map(
                          (__, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton variant="text" />
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))
                  : charges.map((charge: ChargeListItem) => (
                      <ChargeTableRow
                        key={charge.id}
                        charge={charge}
                        onOpenMenu={handleOpenMenu}
                      />
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Action menu */}
        <ChargeActionMenu
          menuAnchor={menuAnchor}
          menuCharge={menuCharge}
          onClose={handleCloseMenu}
          onViewBillingPlan={handleViewBillingPlan}
          onViewAuditLog={handleViewAuditLog}
          onCancelCharge={handleCancelCharge}
          onMarkBillable={handleMarkBillable}
          onMarkNotBillable={handleMarkNotBillable}
          onRevertCancellation={handleRevertCancellation}
        />

        {/* Cancel charge confirmation dialog */}
        <CancelChargeDialog
          isOpen={cancelDialog.isOpen}
          charge={cancelTarget}
          onClose={cancelDialog.close}
          onError={(_, apiStatusCode) => {
            // State-race: hook invalidates the listing so the stale row reconciles.
            if (apiStatusCode === ERROR_CODE_MAPPING.charge_not_cancellable) {
              return;
            }
            setShowSnackbar(true);
          }}
        />

        {/* Revert cancellation confirmation dialog */}
        <RevertChargeCancellationDialog
          isOpen={revertCancellationDialog.isOpen}
          charge={revertCancellationTarget}
          onClose={revertCancellationDialog.close}
          onError={(_, apiStatusCode) => {
            // State-race: hook invalidates the listing so the stale row reconciles.
            if (apiStatusCode === ERROR_CODE_MAPPING.charge_not_revertable) {
              return;
            }
            setShowSnackbar(true);
          }}
        />

        {/* Mark billable dialog (collects bill date, quantity, override) */}
        <MarkChargeBillableDialog
          isOpen={markBillableDialog.isOpen}
          charge={markBillableTarget}
          onClose={markBillableDialog.close}
          onError={(_, apiStatusCode) => {
            // State-race: hook invalidates the listing so the stale row reconciles.
            if (
              apiStatusCode === ERROR_CODE_MAPPING.charge_not_markable_billable
            ) {
              return;
            }
            setShowSnackbar(true);
          }}
        />

        {/* Mark not billable confirmation */}
        <MarkChargeNotBillableDialog
          isOpen={markNotBillableDialog.isOpen}
          charge={markNotBillableTarget}
          onClose={markNotBillableDialog.close}
          onError={(_, apiStatusCode) => {
            // State-race: hook invalidates the listing so the stale row reconciles.
            if (
              apiStatusCode ===
                ERROR_CODE_MAPPING.charge_not_markable_not_billable ||
              apiStatusCode === ERROR_CODE_MAPPING.charge_not_markable_billable
            ) {
              return;
            }
            setShowSnackbar(true);
          }}
        />

        {/* Charge audit log drawer */}
        <ChargeAuditDrawer
          chargeKey={auditTarget?.key ?? null}
          disclosure={auditDrawer}
        />

        <SupportSnackbar
          isOpen={showSnackbar}
          onClose={() => setShowSnackbar(false)}
          message="An unexpected error occurred"
        />

        <TablePaginationFooter
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            onPageChange: handlePageChange,
          }}
          totalCount={totalCount}
          entityOptions={{ singular: 'charge', includeCount: false }}
          downloadURL={downloadURL}
          downloadLabel="Export charge data"
        />
      </Stack>
    </PageContent>
  );
}
